import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  FileCode,
  GitBranch,
  TestTube,
  FileCheck,
  Rocket,
  Bug,
  Sparkles,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const sdlcPhases = [
  {
    phase: 'Planning',
    icon: FileCode,
    color: 'bg-blue-500',
    glowColor: 'shadow-blue-500/50',
    aiIntegrations: [
      'Requirements analysis',
      'User story generation',
      'Architecture suggestions',
    ],
    impact: 'High',
  },
  {
    phase: 'Development',
    icon: GitBranch,
    color: 'bg-electric-cyan',
    glowColor: 'shadow-electric-cyan/50',
    aiIntegrations: [
      'Code generation',
      'Autocomplete',
      'Refactoring assistance',
    ],
    impact: 'Critical',
  },
  {
    phase: 'Testing',
    icon: TestTube,
    color: 'bg-electric-purple',
    glowColor: 'shadow-electric-purple/50',
    aiIntegrations: [
      'Test case generation',
      'Bug prediction',
      'Coverage analysis',
    ],
    impact: 'High',
  },
  {
    phase: 'Review',
    icon: FileCheck,
    color: 'bg-aurora-teal',
    glowColor: 'shadow-aurora-teal/50',
    aiIntegrations: [
      'Code review automation',
      'Security scanning',
      'Best practice checks',
    ],
    impact: 'High',
  },
  {
    phase: 'Deployment',
    icon: Rocket,
    color: 'bg-electric-orange',
    glowColor: 'shadow-electric-orange/50',
    aiIntegrations: [
      'CI/CD optimization',
      'Release notes generation',
      'Change impact analysis',
    ],
    impact: 'Medium',
  },
  {
    phase: 'Maintenance',
    icon: Bug,
    color: 'bg-electric-magenta',
    glowColor: 'shadow-electric-magenta/50',
    aiIntegrations: [
      'Bug triage',
      'Documentation updates',
      'Legacy code analysis',
    ],
    impact: 'High',
  },
]

export default function IntegrationPointsSlide({ isActive: _isActive }: SlideProps) {
  const [activePhase, setActivePhase] = useState<number | null>(null)

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-6 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Integration <span className="text-gradient">Points</span>
            </h2>
            <p className="text-lg text-white/60">
              Where AI fits in the Software Development Lifecycle
            </p>
          </div>
        </StaggerItem>

        {/* SDLC Diagram */}
        <StaggerItem className="flex-1 flex items-center justify-center">
          {/* Fixed size container for the circular diagram */}
          <div className="relative w-[450px] h-[450px] md:w-[500px] md:h-[500px]">
            {/* Orbital ring and connecting lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 500 500">
              <defs>
                <linearGradient id="orbitGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#00f5ff" stopOpacity="0.2" />
                  <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#ff00ff" stopOpacity="0.2" />
                </linearGradient>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#00f5ff" stopOpacity="0.2" />
                </linearGradient>
              </defs>
              {/* Connecting lines from center to each phase */}
              {sdlcPhases.map((_, index) => {
                const angle = (index * 360) / sdlcPhases.length - 90
                const endX = 250 + 180 * Math.cos((angle * Math.PI) / 180)
                const endY = 250 + 180 * Math.sin((angle * Math.PI) / 180)
                return (
                  <motion.line
                    key={index}
                    x1="250"
                    y1="250"
                    x2={endX}
                    y2={endY}
                    stroke="url(#lineGradient)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: activePhase === index ? 1 : 0.3 }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  />
                )
              })}
              <circle cx="250" cy="250" r="180" fill="none" stroke="url(#orbitGradient)" strokeWidth="1" strokeDasharray="8 4" />
            </svg>

            {/* Central AI hub */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
              className="absolute z-20"
              style={{
                left: 250 - 48,
                top: 250 - 48,
              }}
            >
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 30px rgba(139, 92, 246, 0.3)',
                    '0 0 60px rgba(139, 92, 246, 0.5)',
                    '0 0 30px rgba(139, 92, 246, 0.3)',
                  ],
                  rotate: [0, 360],
                }}
                transition={{
                  boxShadow: { duration: 2, repeat: Infinity },
                  rotate: { duration: 20, repeat: Infinity, ease: 'linear' }
                }}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-electric-purple to-electric-magenta flex items-center justify-center"
              >
                <motion.div
                  animate={{ rotate: [0, -360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                >
                  <Sparkles className="w-10 h-10 text-white" />
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Phases positioned in a circle */}
            {sdlcPhases.map((phase, index) => {
              const Icon = phase.icon
              const totalPhases = sdlcPhases.length
              const angle = (index * 360) / totalPhases - 90
              const radius = 180
              const x = radius * Math.cos((angle * Math.PI) / 180)
              const y = radius * Math.sin((angle * Math.PI) / 180)

              // Center of container is 250,250 (half of 500)
              const centerX = 250
              const centerY = 250
              // Node is ~32px wide (w-16), so offset by half
              const nodeOffset = 32

              // Determine tooltip position based on angle
              const isTop = angle >= -135 && angle <= -45
              const isBottom = angle >= 45 && angle <= 135
              const isLeft = angle > 135 || angle < -135

              let tooltipClass = 'left-1/2 -translate-x-1/2 '
              if (isTop) {
                tooltipClass += 'top-full mt-8'
              } else if (isBottom) {
                tooltipClass += 'bottom-full mb-8'
              } else if (isLeft) {
                tooltipClass = 'left-full ml-4 top-1/2 -translate-y-1/2'
              } else {
                tooltipClass = 'right-full mr-4 top-1/2 -translate-y-1/2'
              }

              return (
                <motion.div
                  key={phase.phase}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: centerX + x - nodeOffset,
                    top: centerY + y - nodeOffset,
                  }}
                  onMouseEnter={() => setActivePhase(index)}
                  onMouseLeave={() => setActivePhase(null)}
                  onClick={() => setActivePhase(activePhase === index ? null : index)}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5 + index * 0.1, type: 'spring', stiffness: 300 }}
                    whileHover={{ scale: 1.15 }}
                    className={`relative cursor-pointer transition-all duration-300 flex flex-col items-center ${
                      activePhase === index ? 'z-30' : 'z-10'
                    }`}
                  >
                    {/* Phase node */}
                    <div
                      className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl ${phase.color} flex items-center justify-center transition-all duration-300 ${
                        activePhase === index ? `shadow-lg ${phase.glowColor}` : ''
                      }`}
                    >
                      <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    </div>

                    {/* Phase label */}
                    <span className="mt-2 text-xs md:text-sm font-medium text-white/80 whitespace-nowrap">
                      {phase.phase}
                    </span>

                    {/* Popup on hover */}
                    {activePhase === index && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`absolute ${tooltipClass} glass-strong rounded-xl p-4 min-w-[200px] z-40`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-white">{phase.phase}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            phase.impact === 'Critical' ? 'bg-red-500/20 text-red-400' :
                            phase.impact === 'High' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {phase.impact}
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {phase.aiIntegrations.map((integration) => (
                            <li key={integration} className="text-xs text-white/60 flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-electric-cyan" />
                              {integration}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              )
            })}
          </div>
        </StaggerItem>

        {/* Legend and hint */}
        <StaggerItem>
          <div className="mb-[100px]">
            <div className="flex justify-center gap-6 text-sm mb-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500/50" />
                <span className="text-white/60">Critical Impact</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-orange-500/50" />
                <span className="text-white/60">High Impact</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500/50" />
                <span className="text-white/60">Medium Impact</span>
              </div>
            </div>
            <motion.p
              className="text-center text-sm text-white/40"
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Hover or tap any phase to see AI integrations
            </motion.p>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
